import { useState, useMemo, useEffect, useCallback } from 'react';
import { T, useTranslate } from '@tolgee/react';
import styled from '@emotion/styled';
import { Modal, Checkbox, Tooltip } from 'antd';
import { useMount } from 'ahooks';
import StatusButton from 'components/StatusButton';
import { useAuthorization } from 'components/Authorization';
import { OnOrder } from 'services/api/pay';
import chatttSDK from 'services/chattt';
import { find } from 'lodash-es';

import CurrencyCheckbox from './CurrencyCheckbox';
import PayTypeCheckbox from './PayTypeCheckbox';
import PackageDetail from './PackageDetail';
import StripeSubscriptionModal from './StripeSubscriptionModal';
import ZhixiaGift from './ZhixiaGift';
import usePayPackage from '../hooks/usePayPackage';
import { EnterpriseTotal } from './EnterpriseTotal';
import { NonEnterpriseTotal, NonEnterpriseTotalProps } from './NonEnterpriseTotal';
import { isQuickChat } from 'productLine';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { CompanyAliHelp, Help } from './OrderAI/Help';
import { AILightWeightServiceOverview } from './AIServiceOverview';
import TransComponent from 'components/TransComponent';
import { currencySymbol } from './PriceText';

export const canPayAirwallex = () => {
  if (!chatttSDK) {
    return true;
  }

  const {
    app: { isSupport },
  } = chatttSDK;

  return isSupport && isSupport.openWindowWhitelist;
};

export interface PackageProps {
  packageInfo: Pay.PackageInfoV2;
  onOrder: OnOrder;
  onSubscribed?: () => void;
}

function Package(props: PackageProps) {
  const { packageInfo, onOrder, onSubscribed } = props;

  const { t } = useTranslate();

  const {
    isEnterprise,
    currenciesOptions,
    availablePayTypes,
    currency,
    setCurrency,
    payType,
    setPayType,
    setIsEnterprise,
    bank,
    setBank,
  } = usePayPackage();

  const { payload } = useAuthorization<Oversea.Token.Payload>();
  const { accid = '', orgId } = payload || {};

  const language = useSystemLanguageValue();

  const [accountNum, setAccountNum] = useState(0);
  const [durationNum, setDurationNum] = useState<Pay.DurationNum | -1>(0);
  const [canOrder, setCanOrder] = useState(false);
  const [packageId, setPackageId] = useState<Pay.PackageId | undefined>(() => {
    const { recommendData } = packageInfo;
    const { recommendPackageId } = recommendData;
    return recommendPackageId;
  });

  const [calculatePackage, setCalculatePackage] = useState<
    Pay.CalculatePackagePriceRst | undefined
  >();

  // 默认选中连续包月（自动续费订阅）
  const [isSubscription, setIsSubscription] = useState(true);
  const [ordering, setOrdering] = useState(false);
  const [stripePayUrl, setStripePayUrl] = useState<string>('');
  const [stripeSubscriptionVisible, setStripeSubscriptionVisible] = useState(false);
  const [agreementChecked, setAgreementChecked] = useState(false);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);

  // 实付价格
  const actualPrice = useMemo(() => calculatePackage?.payPrice ?? 0, [calculatePackage?.payPrice]);

  const fetchStripeSign = useCallback(() => {
    const origin =
      import.meta.env.VITE_ENV === 'production'
        ? 'https://mai.zhisuitech.com'
        : 'https://feature-stq-kjds-mai.ftest.leyantech.com';
    // 订阅签约走老订购接口，subscription=true + durationNum=-1，
    // Stripe 支付链接由返回的 paymentUrl 提供
    return onOrder({
      idempotent: `${Date.now()}`,
      accid,
      packageType: isQuickChat ? 3 : 1,
      accountNum,
      durationNum: -1,
      discountTotalPrice: `${actualPrice}`,
      orderAction: 'ORDER',
      payType: 'stripe',
      currency,
      packageId: packageId!,
      language,
      bank,
      subscription: true,
      payResultUrl: `${origin}/#/stripe-subscription-result?result=success`,
      cancelResultUrl: `${origin}/#/stripe-subscription-result?result=cancel`,
    }).then((res) => {
      if (!res?.paymentUrl) {
        throw new Error('no paymentUrl');
      }
      setStripePayUrl(res.paymentUrl);
    });
  }, [accid, accountNum, actualPrice, bank, currency, language, onOrder, packageId]);

  const { packageData, recommendData } = packageInfo;
  const { recommendPackageId, recommendDurationId } = recommendData;

  useMount(() => {
    const recommendPackage = find(packageData, ['id', recommendPackageId]);
    if (recommendPackage) {
      setAccountNum(recommendPackage.accountNum);
      const recommendDuration = find(recommendPackage.durationDesc, ['id', recommendDurationId]);
      setDurationNum(recommendDuration?.durationNum ?? 0);
    }
  });

  useEffect(() => {
    // 连续包月仅支持 Stripe 支付
    if (isSubscription && payType !== 'stripe') {
      setPayType('stripe');
    }
  }, [isSubscription, payType, setPayType]);

  useEffect(() => {
    if (!isSubscription) {
      setAgreementChecked(false);
    }
  }, [isSubscription]);

  const displayedPayTypes = useMemo(() => {
    if (isSubscription) {
      return ['stripe' as const];
    }
    return availablePayTypes;
  }, [isSubscription, availablePayTypes]);
  const totalPrices: NonEnterpriseTotalProps['totalPrices'] = useMemo(() => {
    return {
      actualPrice: calculatePackage?.payPrice ?? 0,
      reducePrice: calculatePackage?.reducePrice ?? 0,
    };
  }, [calculatePackage]);

  if (!orgId) {
    return null;
  }

  let orderElement = null;
  if (isEnterprise) {
    // 企业版
    orderElement = <EnterpriseTotal currency={currency} />;
  } else if (canOrder) {
    orderElement = (
      <>
        <NonEnterpriseTotal currency={currency} totalPrices={totalPrices}>
          {isSubscription ? (
            <SubscriptionInfo>
              <div>
                {t('自动续费')}：{t('phrase.已开启')}
              </div>
              <div>
                {t('下次扣费')}：{calculatePackage?.afterValidEnd}
              </div>
            </SubscriptionInfo>
          ) : (
            <ServiceDate>
              {t('market.服务可使用至', { serviceDate: calculatePackage?.afterValidEnd })}
            </ServiceDate>
          )}
        </NonEnterpriseTotal>
        {isSubscription && (
          <AgreementLine>
            <Checkbox
              checked={agreementChecked}
              onChange={(e) => setAgreementChecked(e.target.checked)}
            >
              <T
                keyName="订购代表接受x"
                params={{
                  x: (
                    <TransComponent
                      content={
                        <Tooltip
                          title={
                            <div style={{ whiteSpace: 'pre-wrap' }}>{t('自动续费协议tips')}</div>
                          }
                        >
                          <AgreementText>{t('自动续费协议')}</AgreementText>
                        </Tooltip>
                      }
                    />
                  ),
                }}
              />
            </Checkbox>
          </AgreementLine>
        )}
        <OrderButton
          type="primary"
          size="large"
          loading={ordering}
          onClick={() => {
            if (payType === 'airwallex' && !canPayAirwallex()) {
              Modal.info({
                title: t('market.当前客户端版本不支持 Airwallex 支付'),
                content: t('market.请重启客户端，升级至最新版本来完成订购'),
              });
              return Promise.resolve();
            }

            if (isSubscription) {
              if (!agreementChecked) {
                setConfirmModalVisible(true);
                return Promise.resolve();
              }
              setOrdering(true);
              return fetchStripeSign()
                .then(() => setStripeSubscriptionVisible(true))
                .catch(() => {})
                .finally(() => setOrdering(false));
            }

            return onOrder({
              idempotent: `${Date.now()}`,
              accid,
              packageType: isQuickChat ? 3 : 1,
              accountNum,
              durationNum,
              discountTotalPrice: `${actualPrice}`,
              orderAction: 'ORDER',
              payType,
              currency,
              packageId: packageId!,
              language,
              bank,
            });
          }}
        >
          {t('market.立即订购')}
        </OrderButton>
      </>
    );
  }
  return (
    <Container>
      <ZhixiaGift />
      <PackageDetail
        orgId={orgId}
        packageInfo={packageInfo}
        currency={currency}
        isEnterprise={isEnterprise}
        setAccountNum={setAccountNum}
        setDurationNum={setDurationNum}
        setCanOrder={setCanOrder}
        setIsEnterprise={setIsEnterprise}
        setIsSubscription={setIsSubscription}
        onPackageChange={(isEnterprise, select) => {
          if (isEnterprise) {
            setCurrency('CNY');
          }
          setPackageId(select?.id);
        }}
        onCalculatePackageChange={setCalculatePackage}
      />
      <CurrencyCheckbox currencies={currenciesOptions} value={currency} onChange={setCurrency} />
      {isEnterprise ? null : (
        <PayTypeCheckbox
          payTypes={displayedPayTypes}
          // 连续包月仅支持 Stripe，且 stripe 不在货币过滤的 availablePayTypes 中
          // （usePayTypeState 会把 payType 覆盖回列表首位），这里直接强制勾选 stripe
          value={isSubscription ? 'stripe' : payType}
          onChange={setPayType}
          currency={currency}
          bank={bank}
          onBankChange={setBank}
        />
      )}
      {currency === 'CNY' && !isSubscription && (
        <div className="mt-[-12px]">
          <span className="mr-[6px]">
            {t('若需要开具公司抬头增值税发票，请使用公司支付宝进行支付')}
          </span>
          <CompanyAliHelp />
          <Help />
        </div>
      )}
      {orderElement}
      <AILightWeightServiceOverview source="orderPackage" />
      {stripeSubscriptionVisible && (
        <StripeSubscriptionModal
          visible={stripeSubscriptionVisible}
          paymentUrl={stripePayUrl}
          accountNum={accountNum}
          packageId={packageId!}
          discountTotalPrice={`${actualPrice}`}
          currency={currency}
          onRefresh={fetchStripeSign}
          onSubscribed={onSubscribed}
          onCancel={() => {
            setStripeSubscriptionVisible(false);
            setStripePayUrl('');
          }}
        />
      )}
      <Modal
        visible={confirmModalVisible}
        closable={false}
        width={400}
        onOk={() => {
          setConfirmModalVisible(false);
          setAgreementChecked(true);
          setOrdering(true);
          fetchStripeSign()
            .then(() => setStripeSubscriptionVisible(true))
            .catch(() => {})
            .finally(() => setOrdering(false));
        }}
        onCancel={() => setConfirmModalVisible(false)}
        okText={t('确认订购')}
        cancelText={t('translation.取消')}
      >
        <T
          keyName="确认订购tips"
          params={{
            x: (
              <TransComponent
                content={
                  <Tooltip
                    title={<div style={{ whiteSpace: 'pre-wrap' }}>{t('自动续费协议tips')}</div>}
                  >
                    <AgreementText>{t('自动续费协议')}</AgreementText>
                  </Tooltip>
                }
              />
            ),
            y: t(`套餐.${packageId}`),
            price: `${currencySymbol[currency]} ${actualPrice}`,
          }}
        />
      </Modal>
    </Container>
  );
}

export default Package;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  > h2 {
    margin-bottom: 12px;
    font-size: 20px;
    color: var(--fett-text-primary);
  }
`;

const OrderButton = styled(StatusButton)`
  width: 260px;
  &,
  &:hover,
  &:active,
  &:focus {
    background-color: var(--fett-app-primary-color);
    border: none;
  }
`;

const ServiceDate = styled.div`
  color: var(--fett-text-tertiary);
`;

const SubscriptionInfo = styled.div`
  color: var(--fett-text-tertiary);
  > div + div {
    margin-top: 4px;
  }
`;

const AgreementLine = styled.div`
  margin-top: -12px;
`;

const AgreementText = styled.span`
  margin: 0 4px;
  color: var(--fett-app-primary-color);
  cursor: pointer;
`;
